import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, StarIcon, ShieldCheckIcon, TrophyIcon } from '@heroicons/react/24/outline';
import KeyBenefitCard from '../components/KeyBenefitCard';
import KeyBenefitModal from '../components/KeyBenefitModal';
import EligibilityCard from '../components/EligibilityCard';
import EligibilityModal from '../components/EligibilityModal';
import ConfirmDialog from '../components/ConfirmDialog';

const RatingCertificateCredentialsPage = () => {
    // Shared State
    const [loading, setLoading] = useState(true);

    // Highlights State (Key Benefits)
    const [highlights, setHighlights] = useState([]);
    const [highlightSettings, setHighlightSettings] = useState({
        section_title: "Aham's Rating Certificate Highlights",
        cta_text: 'AA+', // Used for Seal Grade
        cta_link: 'Credit Rating' // Used for Seal Label
    });
    const [isHighlightModalOpen, setIsHighlightModalOpen] = useState(false);
    const [editingHighlight, setEditingHighlight] = useState(null);
    const [deleteHighlightConfirm, setDeleteHighlightConfirm] = useState({ isOpen: false, id: null });

    // Strengths State (Eligibility)
    const [strengths, setStrengths] = useState([]);
    const [strengthSettings, setStrengthSettings] = useState({
        section_title: 'Key Strengths Cited'
    });
    const [isStrengthModalOpen, setIsStrengthModalOpen] = useState(false);
    const [editingStrength, setEditingStrength] = useState(null);
    const [deleteStrengthConfirm, setDeleteStrengthConfirm] = useState({ isOpen: false, id: null });

    const HIGHLIGHTS_LOAN_TYPE = 'rating_certificate_highlights';
    const STRENGTHS_LOAN_TYPE = 'rating_certificate_strengths';

    // Fetch Data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [
                highlightsRes,
                hSettingsRes,
                strengthsRes,
                sSettingsRes
            ] = await Promise.all([
                axios.get(`/api/admin/key-benefits/${HIGHLIGHTS_LOAN_TYPE}`),
                axios.get(`/api/admin/key-benefits/settings/${HIGHLIGHTS_LOAN_TYPE}`),
                axios.get(`/api/admin/eligibility/${STRENGTHS_LOAN_TYPE}`),
                axios.get(`/api/admin/eligibility/settings/${STRENGTHS_LOAN_TYPE}`)
            ]);

            setHighlights(highlightsRes.data.data || []);
            if (hSettingsRes.data.data) {
                setHighlightSettings(hSettingsRes.data.data);
            }

            setStrengths(strengthsRes.data.data || []);
            if (sSettingsRes.data.data) {
                setStrengthSettings(sSettingsRes.data.data);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load credentials data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ================= HIGHLIGHTS HANDLERS =================
    const handleSaveHighlight = async (formData) => {
        try {
            if (editingHighlight) {
                await axios.put(`/api/admin/key-benefits/${editingHighlight.id}`, formData);
                toast.success('Highlight updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', formData);
                toast.success('Highlight created successfully');
            }
            setIsHighlightModalOpen(false);
            setEditingHighlight(null);
            fetchData();
        } catch (error) {
            console.error('Error saving highlight:', error);
            toast.error('Failed to save highlight');
        }
    };

    const handleDeleteHighlight = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteHighlightConfirm.id}`);
            toast.success('Highlight deleted successfully');
            setDeleteHighlightConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting highlight:', error);
            toast.error('Failed to delete highlight');
        }
    };

    const handleHighlightSettingsSave = async () => {
        try {
            await axios.put(`/api/admin/key-benefits/settings/${HIGHLIGHTS_LOAN_TYPE}`, highlightSettings);
            toast.success('Highlight settings updated');
        } catch (error) {
            console.error('Error saving highlight settings:', error);
            toast.error('Failed to update highlight settings');
        }
    };

    // ================= STRENGTHS HANDLERS =================
    const handleSaveStrength = async (formData) => {
        try {
            if (editingStrength) {
                await axios.put(`/api/admin/eligibility/${editingStrength.id}`, formData);
                toast.success('Strength updated successfully');
            } else {
                await axios.post('/api/admin/eligibility', formData);
                toast.success('Strength created successfully');
            }
            setIsStrengthModalOpen(false);
            setEditingStrength(null);
            fetchData();
        } catch (error) {
            console.error('Error saving strength:', error);
            toast.error('Failed to save strength');
        }
    };

    const handleDeleteStrength = async () => {
        try {
            await axios.delete(`/api/admin/eligibility/${deleteStrengthConfirm.id}`);
            toast.success('Strength deleted successfully');
            setDeleteStrengthConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting strength:', error);
            toast.error('Failed to delete strength');
        }
    };

    const handleStrengthSettingsSave = async () => {
        try {
            await axios.put(`/api/admin/eligibility/settings/${STRENGTHS_LOAN_TYPE}`, strengthSettings);
            toast.success('Strength settings updated');
        } catch (error) {
            console.error('Error saving strength settings:', error);
            toast.error('Failed to update strength settings');
        }
    };


    return (
        <div className="space-y-12">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Our Credentials</h1>
                <p className="text-slate-500 mt-1">Manage Highlights, Rating Seal, and Key Strengths</p>
            </div>

            {/* ================= SECTION 1: HIGHLIGHTS & SEAL ================= */}
            <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                    <h2 className="text-xl font-bold text-slate-700 flex items-center gap-2">
                        <TrophyIcon className="w-6 h-6 text-primary-600" />
                        Rating Certificate Highlights
                    </h2>
                    <button
                        onClick={() => {
                            setEditingHighlight(null);
                            setIsHighlightModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-semibold transition-all shadow-sm"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add Highlight
                    </button>
                </div>

                {/* Highlight Settings (Title & Seal) */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
                    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Section & Seal Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                            <input
                                type="text"
                                value={highlightSettings.section_title}
                                onChange={(e) => setHighlightSettings({ ...highlightSettings, section_title: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Seal Grade (e.g., AA+)</label>
                            <input
                                type="text"
                                value={highlightSettings.cta_text || ''}
                                onChange={(e) => setHighlightSettings({ ...highlightSettings, cta_text: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Seal Label (e.g., Credit Rating)</label>
                            <input
                                type="text"
                                value={highlightSettings.cta_link || ''}
                                onChange={(e) => setHighlightSettings({ ...highlightSettings, cta_link: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button onClick={handleHighlightSettingsSave} className="text-primary-600 font-semibold hover:text-primary-700">Save Highlight Settings</button>
                    </div>
                </div>

                {/* Highlights Grid */}
                {loading ? (
                    <div className="animate-pulse h-32 bg-slate-100 rounded-xl"></div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {highlights.map((item) => (
                            <KeyBenefitCard
                                key={item.id}
                                benefit={item}
                                onEdit={(b) => {
                                    setEditingHighlight(b);
                                    setIsHighlightModalOpen(true);
                                }}
                                onDelete={(id) => setDeleteHighlightConfirm({ isOpen: true, id })}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ================= SECTION 2: KEY STRENGTHS ================= */}
            <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                    <h2 className="text-xl font-bold text-slate-700 flex items-center gap-2">
                        <ShieldCheckIcon className="w-6 h-6 text-green-600" />
                        Key Strengths Cited
                    </h2>
                    <button
                        onClick={() => {
                            setEditingStrength(null);
                            setIsStrengthModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-all shadow-sm"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add Strength
                    </button>
                </div>

                {/* Strength Settings (Title) */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
                    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Section Settings</h3>
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Card Title</label>
                            <input
                                type="text"
                                value={strengthSettings.section_title}
                                onChange={(e) => setStrengthSettings({ ...strengthSettings, section_title: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button onClick={handleStrengthSettingsSave} className="text-green-600 font-semibold hover:text-green-700">Save Strength Settings</button>
                    </div>
                </div>

                {/* Strengths Grid */}
                {loading ? (
                    <div className="animate-pulse h-32 bg-slate-100 rounded-xl"></div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {strengths.map((item) => (
                            <EligibilityCard
                                key={item.id}
                                criteria={item}
                                onEdit={(c) => {
                                    setEditingStrength(c);
                                    setIsStrengthModalOpen(true);
                                }}
                                onDelete={(id) => setDeleteStrengthConfirm({ isOpen: true, id })}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modals & Dialogs */}
            <KeyBenefitModal
                isOpen={isHighlightModalOpen}
                onClose={() => {
                    setIsHighlightModalOpen(false);
                    setEditingHighlight(null);
                }}
                onSubmit={handleSaveHighlight}
                benefit={editingHighlight}
                loanType={HIGHLIGHTS_LOAN_TYPE}
            />

            <EligibilityModal
                isOpen={isStrengthModalOpen}
                onClose={() => {
                    setIsStrengthModalOpen(false);
                    setEditingStrength(null);
                }}
                onSubmit={handleSaveStrength}
                criteria={editingStrength}
                loanType={STRENGTHS_LOAN_TYPE}
            />

            <ConfirmDialog
                isOpen={deleteHighlightConfirm.isOpen}
                onClose={() => setDeleteHighlightConfirm({ isOpen: false, id: null })}
                onConfirm={handleDeleteHighlight}
                title="Delete Highlight"
                message="Are you sure?"
            />

            <ConfirmDialog
                isOpen={deleteStrengthConfirm.isOpen}
                onClose={() => setDeleteStrengthConfirm({ isOpen: false, id: null })}
                onConfirm={handleDeleteStrength}
                title="Delete Strength"
                message="Are you sure?"
            />
        </div>
    );
};

export default RatingCertificateCredentialsPage;
